import { useCallback, useMemo } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";
import * as dayjs from "dayjs";

const SALES_AGGREGATES_COLLECTION = "salesAggregates";

/**
 * Fetcher function for sales aggregates - fetches from both databases
 */
const fetchSalesAggregates = async (startUnix, endUnix) => {
  console.log("[useSalesAggregates] 🔍 Fetching sales aggregates from", startUnix, "to", endUnix);

  try {
    // Fetch from VCE
    const vceSnapshot = await vceFirestore
      .collection(SALES_AGGREGATES_COLLECTION)
      .where("dateUnix", ">=", startUnix)
      .where("dateUnix", "<=", endUnix)
      .orderBy("dateUnix", "asc")
      .get();

    console.log("[useSalesAggregates] 🟢 VCE snapshot size:", vceSnapshot.size);

    const vceData = vceSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      shopSource: "VCE",
    }));

    // Fetch from HVO
    const hvoSnapshot = await hvoFirestore
      .collection(SALES_AGGREGATES_COLLECTION)
      .where("dateUnix", ">=", startUnix)
      .where("dateUnix", "<=", endUnix)
      .orderBy("dateUnix", "asc")
      .get();

    console.log("[useSalesAggregates] 🟣 HVO snapshot size:", hvoSnapshot.size);

    const hvoData = hvoSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      shopSource: "HVO",
    }));

    // Combine data by date
    const combinedData = mergeSalesDataByDate([...vceData, ...hvoData]);
    console.log("[useSalesAggregates] ✅ Total aggregates returned:", combinedData.length);

    return combinedData;
  } catch (error) {
    console.error("[useSalesAggregates] ❌ Error fetching sales aggregates:", error);
    throw error;
  }
};

/**
 * Merge sales data from multiple sources by date
 */
const mergeSalesDataByDate = (salesData) => {
  const mergedMap = {};

  salesData.forEach((item) => {
    const dateKey = item.date;

    if (!mergedMap[dateKey]) {
      mergedMap[dateKey] = {
        date: item.date,
        dateUnix: item.dateUnix,
        dailyTotal: 0,
        totalOrderCount: 0,
        products: {},
        vceLandelijk: {
          orderCount: 0,
          revenue: 0,
          uniqueCustomers: 0,
        },
        hvoLandelijk: {
          orderCount: 0,
          revenue: 0,
          uniqueCustomers: 0,
        },
        vceRegionaal: {
          orderCount: 0,
          revenue: 0,
          uniqueCustomers: 0,
        },
        newCustomers: {
          orderCount: 0,
          revenue: 0,
          uniqueCustomers: 0,
        },
        returningCustomers: {
          orderCount: 0,
          revenue: 0,
          uniqueCustomers: 0,
        },
      };
    }

    // Aggregate orders
    mergedMap[dateKey].totalOrderCount += item.totalOrderCount || 0;

    // HVO is always landelijk - use totalOrderCount and dailyTotal if landelijk field doesn't exist
    if (item.shopSource === "HVO") {
      mergedMap[dateKey].hvoLandelijk.orderCount += item.totalOrderCount || 0;
      mergedMap[dateKey].hvoLandelijk.revenue += item.dailyTotal || 0;
      mergedMap[dateKey].hvoLandelijk.uniqueCustomers += 0;
    }

    // VCE has both landelijk and regionaal
    if (item.shopSource === "VCE") {
      if (item.landelijk) {
        mergedMap[dateKey].vceLandelijk.orderCount += item.landelijk.orderCount || 0;
        mergedMap[dateKey].vceLandelijk.revenue += item.landelijk.revenue || 0;
        mergedMap[dateKey].vceLandelijk.uniqueCustomers += item.landelijk.uniqueCustomers || 0;
      }

      if (item.regionaal) {
        mergedMap[dateKey].vceRegionaal.orderCount += item.regionaal.orderCount || 0;
        mergedMap[dateKey].vceRegionaal.revenue += item.regionaal.revenue || 0;
        mergedMap[dateKey].vceRegionaal.uniqueCustomers += item.regionaal.uniqueCustomers || 0;
      }
    }

    // Calculate dailyTotal as sum of all revenue categories
    mergedMap[dateKey].dailyTotal = 
      (mergedMap[dateKey].vceLandelijk?.revenue || 0) +
      (mergedMap[dateKey].hvoLandelijk?.revenue || 0) +
      (mergedMap[dateKey].vceRegionaal?.revenue || 0);

    // Aggregate new customers data
    if (item.newCustomers) {
      mergedMap[dateKey].newCustomers.orderCount += item.newCustomers.orderCount || 0;
      mergedMap[dateKey].newCustomers.revenue += item.newCustomers.revenue || 0;
      mergedMap[dateKey].newCustomers.uniqueCustomers += item.newCustomers.uniqueCustomers || 0;
    }

    // Aggregate returning customers data
    if (item.returningCustomers) {
      mergedMap[dateKey].returningCustomers.orderCount += item.returningCustomers.orderCount || 0;
      mergedMap[dateKey].returningCustomers.revenue += item.returningCustomers.revenue || 0;
      mergedMap[dateKey].returningCustomers.uniqueCustomers += item.returningCustomers.uniqueCustomers || 0;
    }

    // Merge products
    if (item.products) {
      Object.entries(item.products).forEach(([productId, productData]) => {
        if (!mergedMap[dateKey].products[productId]) {
          mergedMap[dateKey].products[productId] = {
            name: productData.name,
            quantity: 0,
            totalValue: 0,
            weight: 0,
          };
        }

        mergedMap[dateKey].products[productId].quantity += productData.quantity || 0;
        mergedMap[dateKey].products[productId].totalValue += productData.totalValue || 0;
        mergedMap[dateKey].products[productId].weight += productData.weight || 0;
      });
    }
  });

  return Object.values(mergedMap).sort((a, b) => a.dateUnix - b.dateUnix);
};

/**
 * Hook to fetch and manage sales aggregates from both VCE and HVO Firestore databases
 * @param {Object} options - Configuration options
 * @param {number} options.startDate - Start date in unix timestamp
 * @param {number} options.endDate - End date in unix timestamp
 * @param {boolean} options.enabled - Whether to fetch data (default: true)
 * @returns {Object} - Sales aggregates data and utility functions
 */
export const useSalesAggregates = (options = {}) => {
  const { startDate, endDate, enabled = true } = options;

  const startUnix = startDate || dayjs().subtract(30, "day").startOf("day").unix();
  const endUnix = endDate || dayjs().endOf("day").unix();

  console.log("[useSalesAggregates] 📅 Hook initialized with range:", {
    startUnix,
    endUnix,
    enabled,
  });

  // Use SWR for data fetching with caching
  const {
    data: salesAggregates,
    error,
    isLoading,
    mutate,
  } = useSWR(
    enabled ? [`salesAggregates`, startUnix, endUnix] : null,
    ([_, start, end]) => fetchSalesAggregates(start, end),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // Dedupe requests within 60 seconds
    },
  );

  /**
   * Calculate aggregated totals for the period
   */
  const aggregatedTotals = useMemo(() => {
    if (!salesAggregates || salesAggregates.length === 0) {
      return {
        totalRevenue: 0,
        totalOrders: 0,
        vceLandelijkOrders: 0,
        vceLandelijkRevenue: 0,
        hvoLandelijkOrders: 0,
        hvoLandelijkRevenue: 0,
        vceRegionaalOrders: 0,
        vceRegionaalRevenue: 0,
        newOrders: 0,
        newRevenue: 0,
        newCustomers: 0,
        returningOrders: 0,
        returningRevenue: 0,
        returningCustomers: 0,
      };
    }

    return salesAggregates.reduce(
      (totals, day) => {
        totals.totalRevenue += day.dailyTotal || 0;
        totals.totalOrders += day.totalOrderCount || 0;
        totals.vceLandelijkOrders += day.vceLandelijk?.orderCount || 0;
        totals.vceLandelijkRevenue += day.vceLandelijk?.revenue || 0;
        totals.hvoLandelijkOrders += day.hvoLandelijk?.orderCount || 0;
        totals.hvoLandelijkRevenue += day.hvoLandelijk?.revenue || 0;
        totals.vceRegionaalOrders += day.vceRegionaal?.orderCount || 0;
        totals.vceRegionaalRevenue += day.vceRegionaal?.revenue || 0;
        totals.newOrders += day.newCustomers?.orderCount || 0;
        totals.newRevenue += day.newCustomers?.revenue || 0;
        totals.newCustomers += day.newCustomers?.uniqueCustomers || 0;
        totals.returningOrders += day.returningCustomers?.orderCount || 0;
        totals.returningRevenue += day.returningCustomers?.revenue || 0;
        totals.returningCustomers += day.returningCustomers?.uniqueCustomers || 0;
        return totals;
      },
      {
        totalRevenue: 0,
        totalOrders: 0,
        vceLandelijkOrders: 0,
        vceLandelijkRevenue: 0,
        hvoLandelijkOrders: 0,
        hvoLandelijkRevenue: 0,
        vceRegionaalOrders: 0,
        vceRegionaalRevenue: 0,
        newOrders: 0,
        newRevenue: 0,
        newCustomers: 0,
        returningOrders: 0,
        returningRevenue: 0,
        returningCustomers: 0,
      },
    );
  }, [salesAggregates]);

  /**
   * Calculate average order value
   */
  const averageOrderValue = useMemo(() => {
    if (!aggregatedTotals || aggregatedTotals.totalOrders === 0) return 0;
    return aggregatedTotals.totalRevenue / aggregatedTotals.totalOrders;
  }, [aggregatedTotals]);

  /**
   * Get top selling products for the period
   */
  const getTopProducts = useCallback(
    (limit = 10) => {
      if (!salesAggregates) return [];

      const productMap = {};

      salesAggregates.forEach((day) => {
        if (day.products) {
          Object.entries(day.products).forEach(([productId, productData]) => {
            if (!productMap[productId]) {
              productMap[productId] = {
                id: productId,
                name: productData.name,
                totalQuantity: 0,
                totalRevenue: 0,
                totalWeight: 0,
              };
            }

            productMap[productId].totalQuantity += productData.quantity || 0;
            productMap[productId].totalRevenue += productData.totalValue || 0;
            productMap[productId].totalWeight += productData.weight || 0;
          });
        }
      });

      return Object.values(productMap)
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, limit);
    },
    [salesAggregates],
  );

  /**
   * Aggregate data by week or month based on duration
   */
  const aggregateDataByPeriod = useCallback((salesData, duration) => {
    if (!salesData || salesData.length === 0) return salesData;

    // If duration is less than 60 days, show daily data
    if (duration < 60) return salesData;

    // If duration is less than 180 days, aggregate by week
    if (duration < 180) {
      const weekMap = {};
      salesData.forEach((item) => {
        const weekKey = dayjs(item.date, "DD-MM-YYYY").startOf("isoWeek").format("DD-MM-YYYY");
        if (!weekMap[weekKey]) {
          weekMap[weekKey] = {
            date: `Week ${dayjs(item.date, "DD-MM-YYYY").isoWeek()}`,
            dateUnix: dayjs(item.date, "DD-MM-YYYY").startOf("isoWeek").unix(),
            dailyTotal: 0,
            totalOrderCount: 0,
            vceLandelijk: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
            hvoLandelijk: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
            vceRegionaal: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
            newCustomers: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
            returningCustomers: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
          };
        }
        weekMap[weekKey].dailyTotal += item.dailyTotal || 0;
        weekMap[weekKey].totalOrderCount += item.totalOrderCount || 0;
        weekMap[weekKey].vceLandelijk.orderCount += item.vceLandelijk?.orderCount || 0;
        weekMap[weekKey].vceLandelijk.revenue += item.vceLandelijk?.revenue || 0;
        weekMap[weekKey].vceLandelijk.uniqueCustomers += item.vceLandelijk?.uniqueCustomers || 0;
        weekMap[weekKey].hvoLandelijk.orderCount += item.hvoLandelijk?.orderCount || 0;
        weekMap[weekKey].hvoLandelijk.revenue += item.hvoLandelijk?.revenue || 0;
        weekMap[weekKey].hvoLandelijk.uniqueCustomers += item.hvoLandelijk?.uniqueCustomers || 0;
        weekMap[weekKey].vceRegionaal.orderCount += item.vceRegionaal?.orderCount || 0;
        weekMap[weekKey].vceRegionaal.revenue += item.vceRegionaal?.revenue || 0;
        weekMap[weekKey].vceRegionaal.uniqueCustomers += item.vceRegionaal?.uniqueCustomers || 0;
        weekMap[weekKey].newCustomers.orderCount += item.newCustomers?.orderCount || 0;
        weekMap[weekKey].newCustomers.revenue += item.newCustomers?.revenue || 0;
        weekMap[weekKey].newCustomers.uniqueCustomers += item.newCustomers?.uniqueCustomers || 0;
        weekMap[weekKey].returningCustomers.orderCount += item.returningCustomers?.orderCount || 0;
        weekMap[weekKey].returningCustomers.revenue += item.returningCustomers?.revenue || 0;
        weekMap[weekKey].returningCustomers.uniqueCustomers += item.returningCustomers?.uniqueCustomers || 0;
      });
      return Object.values(weekMap).sort((a, b) => a.dateUnix - b.dateUnix);
    }

    // For longer periods, aggregate by month
    const monthMap = {};
    salesData.forEach((item) => {
      const monthKey = dayjs(item.date, "DD-MM-YYYY").startOf("month").format("DD-MM-YYYY");
      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          date: dayjs(item.date, "DD-MM-YYYY").format("MMM YYYY"),
          dateUnix: dayjs(item.date, "DD-MM-YYYY").startOf("month").unix(),
          dailyTotal: 0,
          totalOrderCount: 0,
          vceLandelijk: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
          hvoLandelijk: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
          vceRegionaal: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
          newCustomers: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
          returningCustomers: { orderCount: 0, revenue: 0, uniqueCustomers: 0 },
        };
      }
      monthMap[monthKey].dailyTotal += item.dailyTotal || 0;
      monthMap[monthKey].totalOrderCount += item.totalOrderCount || 0;
      monthMap[monthKey].vceLandelijk.orderCount += item.vceLandelijk?.orderCount || 0;
      monthMap[monthKey].vceLandelijk.revenue += item.vceLandelijk?.revenue || 0;
      monthMap[monthKey].vceLandelijk.uniqueCustomers += item.vceLandelijk?.uniqueCustomers || 0;
      monthMap[monthKey].hvoLandelijk.orderCount += item.hvoLandelijk?.orderCount || 0;
      monthMap[monthKey].hvoLandelijk.revenue += item.hvoLandelijk?.revenue || 0;
      monthMap[monthKey].hvoLandelijk.uniqueCustomers += item.hvoLandelijk?.uniqueCustomers || 0;
      monthMap[monthKey].vceRegionaal.orderCount += item.vceRegionaal?.orderCount || 0;
      monthMap[monthKey].vceRegionaal.revenue += item.vceRegionaal?.revenue || 0;
      monthMap[monthKey].vceRegionaal.uniqueCustomers += item.vceRegionaal?.uniqueCustomers || 0;
      monthMap[monthKey].newCustomers.orderCount += item.newCustomers?.orderCount || 0;
      monthMap[monthKey].newCustomers.revenue += item.newCustomers?.revenue || 0;
      monthMap[monthKey].newCustomers.uniqueCustomers += item.newCustomers?.uniqueCustomers || 0;
      monthMap[monthKey].returningCustomers.orderCount += item.returningCustomers?.orderCount || 0;
      monthMap[monthKey].returningCustomers.revenue += item.returningCustomers?.revenue || 0;
      monthMap[monthKey].returningCustomers.uniqueCustomers += item.returningCustomers?.uniqueCustomers || 0;
    });
    return Object.values(monthMap).sort((a, b) => a.dateUnix - b.dateUnix);
  }, []);

  /**
   * Calculate duration in days
   */
  const duration = useMemo(() => {
    return Math.ceil((endUnix - startUnix) / 86400);
  }, [startUnix, endUnix]);

  /**
   * Get aggregated sales data based on duration
   */
  const aggregatedData = useMemo(() => {
    return aggregateDataByPeriod(salesAggregates, duration);
  }, [salesAggregates, duration, aggregateDataByPeriod]);

  /**
   * Get daily revenue data for charts
   */
  const getDailyRevenueData = useCallback(() => {
    if (!aggregatedData) return { labels: [], data: [] };

    const labels = aggregatedData.map((item) => item.date);
    const data = aggregatedData.map((item) => item.dailyTotal || 0);

    return { labels, data };
  }, [aggregatedData]);

  /**
   * Get daily orders data for charts
   */
  const getDailyOrdersData = useCallback(() => {
    if (!aggregatedData) return { labels: [], data: [] };

    const labels = aggregatedData.map((item) => item.date);
    const data = aggregatedData.map((item) => item.totalOrderCount || 0);

    return { labels, data };
  }, [aggregatedData]);

  /**
   * Get landelijk vs regionaal data for charts with shop source breakdown
   */
  const getLandelijkVsRegionaalData = useCallback(() => {
    if (!aggregatedData) return { labels: [], vceLandelijk: [], hvoLandelijk: [], vceRegionaal: [] };

    const labels = aggregatedData.map((item) => item.date);
    const vceLandelijk = aggregatedData.map((item) => item.vceLandelijk?.revenue || 0);
    const hvoLandelijk = aggregatedData.map((item) => item.hvoLandelijk?.revenue || 0);
    const vceRegionaal = aggregatedData.map((item) => item.vceRegionaal?.revenue || 0);

    return { labels, vceLandelijk, hvoLandelijk, vceRegionaal };
  }, [aggregatedData]);

  /**
   * Get new vs returning customers data for charts
   */
  const getNewVsReturningData = useCallback(() => {
    if (!aggregatedData) return { labels: [], newCustomers: [], returningCustomers: [] };

    const labels = aggregatedData.map((item) => item.date);
    const newCustomers = aggregatedData.map((item) => item.newCustomers?.revenue || 0);
    const returningCustomers = aggregatedData.map((item) => item.returningCustomers?.revenue || 0);

    return { labels, newCustomers, returningCustomers };
  }, [aggregatedData]);

  /**
   * Get new customer metrics per bucket (day/week/month) for trend charts
   */
  const getNewCustomerTrendData = useCallback(() => {
    if (!aggregatedData) return { labels: [], customers: [], orders: [], revenue: [], customerShare: [] };

    const labels = aggregatedData.map((item) => item.date);
    const customers = aggregatedData.map((item) => item.newCustomers?.uniqueCustomers || 0);
    const orders = aggregatedData.map((item) => item.newCustomers?.orderCount || 0);
    const revenue = aggregatedData.map((item) => item.newCustomers?.revenue || 0);
    const customerShare = aggregatedData.map((item) => {
      const newUnique = item.newCustomers?.uniqueCustomers || 0;
      const returningUnique = item.returningCustomers?.uniqueCustomers || 0;
      const total = newUnique + returningUnique;
      return total > 0 ? parseFloat(((newUnique / total) * 100).toFixed(1)) : 0;
    });

    return { labels, customers, orders, revenue, customerShare };
  }, [aggregatedData]);

  /**
   * Manually refresh sales aggregates
   */
  const refreshAggregates = useCallback(() => {
    return mutate();
  }, [mutate]);

  return {
    salesAggregates: aggregatedData || [],
    rawSalesAggregates: salesAggregates || [],
    isLoading,
    error,
    ...aggregatedTotals,
    averageOrderValue,
    getTopProducts,
    getDailyRevenueData,
    getDailyOrdersData,
    getLandelijkVsRegionaalData,
    getNewVsReturningData,
    getNewCustomerTrendData,
    refreshAggregates,
  };
};
